import toast from 'react-hot-toast';

interface ErrorContext {
  action: string;
  suggestion?: string;
}

interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      error?: string;
    };
  };
  message?: string;
  code?: string;
}

// Maps HTTP status codes to user-friendly messages with suggestions
const STATUS_MESSAGES: Record<number, { message: string; suggestion: string }> = {
  400: {
    message: 'Invalid request',
    suggestion: 'Please check your input and try again.',
  },
  401: {
    message: 'Session expired',
    suggestion: 'Please log in again to continue.',
  },
  403: {
    message: 'Access denied',
    suggestion: 'You may need to upgrade your plan or contact support.',
  },
  404: {
    message: 'Not found',
    suggestion: 'The requested resource may have been moved or deleted.',
  },
  409: {
    message: 'Conflict',
    suggestion: 'This action conflicts with existing data. Please refresh and try again.',
  },
  429: {
    message: 'Too many requests',
    suggestion: 'Please wait a moment before trying again.',
  },
  500: {
    message: 'Server error',
    suggestion: 'Our team has been notified. Please try again later.',
  },
  502: {
    message: 'Service unavailable',
    suggestion: 'Please check your connection and try again.',
  },
  503: {
    message: 'Service temporarily unavailable',
    suggestion: 'We\'re performing maintenance. Please try again in a few minutes.',
  },
};

// Maps error codes to user-friendly messages
const ERROR_CODE_MESSAGES: Record<string, { message: string; suggestion: string }> = {
  NETWORK_ERROR: {
    message: 'Network connection failed',
    suggestion: 'Please check your internet connection and try again.',
  },
  ECONNREFUSED: {
    message: 'Unable to connect to server',
    suggestion: 'The server may be down. Please try again later.',
  },
  TIMEOUT: {
    message: 'Request timed out',
    suggestion: 'The operation is taking too long. Please try again.',
  },
};

/**
 * Handles API errors and displays appropriate toast notifications
 * with actionable suggestions for users.
 */
export function handleApiError(error: ApiError, context: ErrorContext): void {
  const { action, suggestion: customSuggestion } = context;

  // Get the error details
  const status = error.response?.status;
  const serverMessage = error.response?.data?.message || error.response?.data?.error;
  const errorCode = error.code;

  // Title is constructed for potential future use
  void `Failed to ${action}`;
  let message = '';
  let suggestion = customSuggestion || '';

  // Check for specific HTTP status codes
  if (status && STATUS_MESSAGES[status]) {
    const statusInfo = STATUS_MESSAGES[status];
    message = serverMessage || statusInfo.message;
    suggestion = suggestion || statusInfo.suggestion;
  }
  // Check for error codes (network issues, etc.)
  else if (errorCode && ERROR_CODE_MESSAGES[errorCode]) {
    const codeInfo = ERROR_CODE_MESSAGES[errorCode];
    message = codeInfo.message;
    suggestion = suggestion || codeInfo.suggestion;
  }
  // Check for network errors
  else if (!error.response && error.message) {
    if (error.message.includes('Network Error') || error.message.includes('network')) {
      message = 'Network connection failed';
      suggestion = suggestion || 'Please check your internet connection and try again.';
    } else {
      message = error.message;
      suggestion = suggestion || 'Please try again or contact support if the issue persists.';
    }
  }
  // Fallback to server message or generic error
  else {
    message = serverMessage || 'An unexpected error occurred';
    suggestion = suggestion || 'Please try again or contact support if the issue persists.';
  }

  // Display toast with both message and suggestion
  toast.error(
    `${message}${suggestion ? `\n${suggestion}` : ''}`,
    {
      duration: 5000,
      style: {
        maxWidth: '400px',
        whiteSpace: 'pre-line',
      },
    }
  );

  // Log error for debugging
  console.error(`[${action}] Error:`, {
    status,
    message,
    serverMessage,
    errorCode,
    fullError: error,
  });
}

/**
 * Creates a custom toast with title and message
 */
export function showErrorToast(_title: string, message: string, suggestion?: string): void {
  const fullMessage = suggestion ? `${message}\n${suggestion}` : message;
  toast.error(fullMessage, {
    duration: 5000,
    style: {
      maxWidth: '400px',
      whiteSpace: 'pre-line',
    },
  });
}

/**
 * Shows a success toast with optional follow-up action suggestion
 */
export function showSuccessToast(message: string, nextStep?: string): void {
  const fullMessage = nextStep ? `${message}\n${nextStep}` : message;
  toast.success(fullMessage, {
    duration: 4000,
    style: {
      maxWidth: '400px',
      whiteSpace: 'pre-line',
    },
  });
}

export default handleApiError;
